<template>
    <div class="row py-5">
        <div class="col-12 px-5">
            <div class="row align-items-center mb-4 gy-3">
                <div class="col-12 col-md-6">
                    <h2 class="mb-0">{{ $t("customer.customerDetail") }} - {{ customer.intern_id }}</h2>
                </div>
                <div class="col-12 col-md-6 d-flex justify-content-md-end gap-2">
                    <button v-if="hasPerm()" class="btn button-top d-flex align-items-center justify-content-center"
                        @click="changeToContact()">
                        <span>{{ customer.to_contact ? $t("customer.dontContact") : $t("customer.toContact") }}</span>
                    </button>
                    <router-link v-if="$store.getters.hasPerm('change_customer')"
                        :to="'/customer/' + customerId + '/edit'"
                        class="btn button-top d-flex align-items-center justify-content-center">
                        <icon icon="Edit" style="width: 16px; height: 16px;" class="me-1" />
                        {{ $t("utils.edit") }}
                    </router-link>
                </div>
            </div>

            <div class="row g-4">
                <div class="col-12 col-lg-6">
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-body">
                            <h3 class="h5 mb-3">{{ $t("customer.customer") }}</h3>
                            <div class="d-flex justify-content-between py-2">
                                <span class="text-muted">{{ $t("customer.firstName") }}</span>
                                <span class="fw-semibold">{{ customer.first_name }} {{ customer.last_name }}</span>
                            </div>
                            <div v-if="customer.organization_id" class="d-flex justify-content-between py-2">
                                <span class="text-muted">{{ $t("customer.organizationId") }}</span>
                                <span class="fw-semibold">{{ customer.organization_id }}</span>
                            </div>
                            <div v-if="customer.organization_name" class="d-flex justify-content-between py-2">
                                <span class="text-muted">{{ $t("customer.organizationName") }}</span>
                                <span class="fw-semibold">{{ customer.organization_name }}</span>
                            </div>
                            <div class="d-flex justify-content-between py-2">
                                <span class="text-muted">{{ $t("customer.phone") }}</span>
                                <a :href="'tel:' + customer.whole_phone" class="fw-semibold link-darker">
                                    {{ customer.whole_phone }}
                                </a>
                            </div>
                            <div v-if="customer.email" class="d-flex justify-content-between py-2">
                                <span class="text-muted">{{ $t("customer.email") }}</span>
                                <a :href="'mailto:' + customer.email" class="fw-semibold link-darker">
                                    {{ customer.email }}
                                </a>
                            </div>
                            <div class="d-flex justify-content-between py-2">
                                <span class="text-muted">{{ $t("customer.address") }}</span>
                                <span class="fw-semibold text-end">
                                    {{ customer.address ? customer.address.full_address : '-' }}
                                    <a v-if="customer.address" :href="customer.address.create_waze_link"
                                        class="link-darker d-block small" target="_blank">
                                        {{ $t("customer.openWaze") }}
                                    </a>
                                </span>
                            </div>
                            <div class="d-flex justify-content-between align-items-center py-2">
                                <span class="text-muted">{{ $t("customer.toContact") }}</span>
                                <span class="badge" :class="customer.to_contact ? 'bg-success' : 'bg-secondary'">
                                    {{ customer.to_contact ? $t("orders.yes") : $t("orders.no") }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12 col-lg-6">
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-body">
                            <div id="customer-map" style="height:300px; max-width: 100%;"></div>
                            <div ref="map"></div>
                        </div>
                    </div>
                </div>

                <div v-if="hasPerm()" class="col-12 col-lg-6">
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-body">
                            <h3 class="h5 mb-3">{{ $t("customer.internNotes") }}</h3>
                            <div class="mb-3">
                                <textarea v-model="newNoteText" class="form-control" rows="2"
                                    :placeholder="$t('customer.addNotePlaceholder')"></textarea>
                                <button class="btn form-submit mt-2" :disabled="!newNoteText"
                                    @click="addNote()">{{ $t("customer.addNote") }}</button>
                            </div>
                            <div v-if="!customer.notes || customer.notes.length === 0" class="text-muted">
                                {{ $t("customer.noNotes") }}
                            </div>
                            <ul v-else class="list-unstyled">
                                <li v-for="note in customer.notes" :key="note.id"
                                    class="d-flex justify-content-between align-items-start py-2">
                                    <div>
                                        <div>{{ note.text }}</div>
                                        <small class="text-muted">
                                            {{ note.created_by ? note.created_by.first_name + " " + note.created_by.last_name : "" }} - {{ note.created }}
                                        </small>
                                    </div>
                                    <icon icon="Delete" style="width: 20px; height: 20px; cursor: pointer; flex-shrink: 0;"
                                        class="ms-2" @click="deleteNote(note.id)" />
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div v-if="$store.getters.hasPerm('view_order')" class="col-12">
                    <div class="card border-0 shadow-sm">
                        <div class="card-body">
                            <h3 class="h5 mb-3">{{ $t("customer.orderList") }}</h3>
                            <div v-if="orders.length === 0" class="text-muted">
                                {{ $t("customer.noOrders") }}
                            </div>
                            <div v-else class="table-responsive">
                                <table class="table table-hover table-borderless align-middle">
                                    <thead>
                                        <tr class="text-muted small">
                                            <th>{{ $t("customer.id") }}</th>
                                            <th>{{ $t("customer.dateOfCreation") }}</th>
                                            <th>{{ $t("customer.dateOfDelivery") }}</th>
                                            <th>{{ $t("customer.address") }}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="order in orders" :key="order.id">
                                            <td>{{ order.intern_id }}</td>
                                            <td>{{ order.created }}</td>
                                            <td>{{ order.expected_delivery_date }}</td>
                                            <td>{{ customer.address ? customer.address.full_address : 'N/A' }}</td>
                                            <td class="text-end">
                                                <a :href="'/order/' + order.id + '/copy'"
                                                    class="btn btn-sm btn-outline-primary me-1">
                                                    {{ $t("utils.copy") }}
                                                </a>
                                                <router-link :to="{ name: 'OrderDetail', params: { id: order.id } }"
                                                    class="btn btn-sm btn-outline-primary me-1">
                                                    {{ $t("utils.detail") }}
                                                </router-link>
                                                <router-link :to="{ name: 'UpdateOrderItems', params: { id: order.id } }"
                                                    class="btn btn-sm btn-outline-primary">
                                                    {{ $t("utils.edit") }}
                                                </router-link>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12">
                    <div class="card border-0 shadow-sm">
                        <div class="card-body">
                            <FilesContainer :files="customer.files" :customerFileContainer="true"
                                @uploadFile="uploadFile" @deleteFile="deleteFile" @downloadFile="downloadFile" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import FilesContainer from "@/components/FilesContainer.vue";
export default {
    components: {
        FilesContainer
    },
    data() {
        return {
            customerId: null,
            customer: {},
            orders: [],
            map: null,
            newNoteText: "",
        };
    },
    created: function () {
        this.customerId = this.$route.params.id;
        this.loadData();
    },
    methods: {
        loadData: function () {
            this.$store.getters.api
                .get("/customer/" + this.customerId)
                .then((response) => {
                    this.customer = response.data.customer;
                    this.orders = response.data.orders;
                    this.initializeMap();
                })
                .catch((response) => {
                    alert(this.$t("error." + response?.response?.data?.error_code));
                    var status = response?.response?.status;
                    if (status == 404) {
                        this.$router.push({ name: "Customers" });
                    }
                });
        },
        async initializeMap() {
            if (
                !this.customer.address ||
                !this.customer.address.gps_lat ||
                !this.customer.address.gps_lon
            ) {
                return;
            }

            const lat = this.customer.address.gps_lat;
            const lon = this.customer.address.gps_lon;

            // if map already exists, just update view and marker
            if (this.map) {
                this.map.setView([lat, lon], 15);
                this.marker.setLatLng([lat, lon]);
                return;
            }

            // create map
            this.map = L.map("customer-map").setView([lat, lon], 15);

            // load TileJSON from Mapy.cz
            const response = await fetch(
                "https://api.mapy.com/v1/maptiles/basic/tiles.json?apikey=vDDn3L5W7ZhRW9u7EowHsICpRmY_vEBMIqU00aGYydo"
            );

            const tileJson = await response.json();

            // add tile layer
            L.tileLayer(tileJson.tiles[0], {
                minZoom: tileJson.minzoom,
                maxZoom: tileJson.maxzoom,
                attribution: tileJson.attribution,
            }).addTo(this.map);

            // set marker
            this.marker = L.marker([lat, lon]).addTo(this.map);
        },
        changeToContact() {
            this.$store.getters.api.put("/customer/" + this.customerId + "/change_to_contact").then((response) => {
                this.loadData();
            });
        },
        addNote() {
            this.$store.getters.api.post("/customer/" + this.customerId + "/notes/add", { text: this.newNoteText }).then((response) => {
                this.newNoteText = "";
                this.loadData();
            })
        },
        deleteNote(id) {
            this.$store.getters.api.delete("/customer/" + this.customerId + "/notes/" + id + "/delete").then((response) => {
                this.loadData();
            })
        },
        hasPerm: function () {
            var perm = false;
            this.$store.state.user.functions.forEach(element => {
                if (element.name == "owner" || element.name == "dispatcher") {
                    perm = true;
                }
            })
            return perm;
        },
        uploadFile: function (data) {
            const formData = new FormData();
            formData.append("title", data.title);
            formData.append("file_type", data.file_type);
            formData.append("file", data.file);

            this.$store.getters.api.post("/customer/" + this.customerId + "/files/add", formData).then((response) => {
                this.loadData();
            })
        },
        deleteFile: function (id) {
            this.$store.getters.api.delete("/customer/" + this.customerId + "/files/" + id + "/delete").then((response) => {
                this.loadData();
            })
        },
        downloadFile: function (documentItem) {
            const fileName = documentItem.name || 'downloaded_document';

            this.$store.getters.api.get("/customer/" + this.customerId + "/files/" + documentItem.id + "/download", {
                responseType: 'blob'
            }
            ).then((response) => {
                if (response.data) {
                    const documentItem = response.data;

                    // Create a Blob URL using the provided Blob
                    const url = window.URL.createObjectURL(new Blob([response.data]));

                    // Create a link element to trigger the download
                    const link = window.document.createElement('a');

                    const fileExtension = documentItem.type.split('/')[1];  // Extract extension (e.g., 'pdf' from 'application/pdf')
                    link.href = url;

                    // Use the Blob type to determine the filename with extension
                    link.setAttribute('download', `${fileName}.${fileExtension}`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();

                    this.loadData();
                }
            })
        }
    },
};
</script>
<style lang="scss"></style>